import { useEffect, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { toast } from "sonner";
import { money, num, pct } from "@/lib/format";
import {
  Flame, Zap, Gem, Trophy, Heart, Flag, Star, Award, Crown, Sparkles,
  RefreshCw, CalendarCheck, TrendingUp, Loader2,
} from "lucide-react";

const BADGE_ICONS = { flag: Flag, star: Star, award: Award, crown: Crown, flame: Flame, zap: Zap, gem: Gem, trophy: Trophy, heart: Heart };

function fireConfetti() {
  const end = Date.now() + 1400;
  const colors = ["#007AFF", "#22C55E", "#F59E0B", "#FFFFFF"];
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 60, startVelocity: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 5, angle: 120, spread: 60, startVelocity: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

const STORE_KEY = "phs_celebrated_accepts";

export default function RepMotivation() {
  const [data, setData] = useState(null);
  const [hype, setHype] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const celebrateAccepts = useCallback((accepts) => {
    if (!accepts?.length) return;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(STORE_KEY) || "[]"); } catch { seen = []; }
    const fresh = accepts.filter((a) => !seen.includes(a.id));
    if (fresh.length) {
      fireConfetti();
      const a = fresh[0];
      toast.success(
        `🎉 ${a.customer || "A customer"} accepted ${a.quote_number}${a.sell_price ? ` — ${money(a.sell_price)}` : ""}! Nice close!`,
        { duration: 7000 },
      );
      localStorage.setItem(STORE_KEY, JSON.stringify([...seen, ...fresh.map((x) => x.id)].slice(-200)));
    }
  }, []);

  useEffect(() => {
    api.get("/motivation/me")
      .then((r) => { setData(r.data); setHype(r.data.hype); celebrateAccepts(r.data.recent_accepts); })
      .catch(() => {});
  }, [celebrateAccepts]);

  const refreshHype = async () => {
    setRefreshing(true);
    try {
      const { data: h } = await api.get("/motivation/hype?event=dashboard&ai=true");
      if (h?.text) setHype(h);
    } catch { /* keep existing */ }
    finally { setRefreshing(false); }
  };

  if (!data || !hype) return null;
  const s = data.stats;
  const fun = hype.tone === "fun";

  return (
    <div className="space-y-3" data-testid="rep-motivation">
      {/* Hype banner */}
      <div
        data-testid="hype-banner"
        className={`relative overflow-hidden rounded-lg border px-5 py-4 ${
          fun
            ? "border-transparent bg-gradient-to-r from-[hsl(var(--primary))] to-[#0A4FB0] text-white"
            : "border-border bg-card"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${fun ? "bg-white/20" : "bg-[hsl(var(--primary))]/10"}`}>
              {fun ? <Flame className="h-5 w-5" /> : <Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" />}
            </div>
            <div>
              <p className={`text-base font-bold leading-snug ${fun ? "text-white" : "text-foreground"}`} data-testid="hype-text">{hype.text}</p>
              <p className={`text-xs ${fun ? "text-white/70" : "text-muted-foreground"}`}>
                {num(s.today)} today · {num(s.this_week)} this week · {num(s.total)} all-time
              </p>
            </div>
          </div>
          <button
            data-testid="hype-refresh"
            onClick={refreshHype}
            disabled={refreshing}
            title="New hype line (AI)"
            className={`shrink-0 rounded-full p-2 transition-colors ${fun ? "hover:bg-white/20" : "hover:bg-secondary"}`}
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Streaks & badges */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4" data-testid="streaks-widget">
        <Stat icon={Flame} label="Day Streak" value={`${num(s.day_streak)}d`} hot={s.day_streak >= 3} />
        <Stat icon={Zap} label="Win Streak" value={num(s.win_streak)} hot={s.win_streak >= 3} />
        <Stat icon={TrendingUp} label="Best Margin" value={pct(s.best_margin)} />
        <Stat icon={CalendarCheck} label="Quotes / Week" value={num(s.this_week)} />
      </div>

      {data.badges.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="badges-row">
          {data.badges.map((b) => {
            const Icon = BADGE_ICONS[b.icon] || Award;
            return (
              <span key={b.id} data-testid={`badge-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/8 px-2.5 py-1 text-xs font-semibold text-[hsl(var(--primary))]">
                <Icon className="h-3.5 w-3.5" /> {b.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, hot }) {
  return (
    <div className="bg-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${hot ? "text-[hsl(var(--warning))]" : ""}`} /> {label}
      </div>
      <div className={`metric-num mt-1 text-xl font-bold ${hot ? "text-[hsl(var(--warning))]" : ""}`}>{value}</div>
    </div>
  );
}
